import { useAppContext } from '@/AppContext';
import { getSessionData } from '@/auth/authUtil';
import { conversatonType, questionSamples } from '@/config';
import { useCheckCount } from '@/hooks/useCheckCount';
import { useUpload } from '@/hooks/useUpload';
import { validateToken } from '@/pages/login/api';
import { RootState } from '@/typings';
import { formatNumber } from '@/util';
import { ElButton, ElIcon, ElInput, ElLink, ElRadioGroup, ElRow, ElSwitch, ElTag, ElTooltip, ElUpload, isEmpty, randomCode } from '@qsxy/element-plus-react';
import { Radio } from '@qsxy/element-plus-react/dist/Radio';
import { RadioGroup } from '@qsxy/element-plus-react/dist/RadioGroup';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

type Props = { showSample?: boolean; onSend?: () => void };

const Conversation = ({ showSample = true, onSend }: Props) => {
	const navigate = useNavigate();
	const loginUser = useSelector<RootState, RootState['user']>(state => state.user ?? {});

	const [type, setType] = useState('all');
	const [deep, setDeep] = useState(false);
	const [question, setQuestion] = useState('');
	const [chatType, setChatType] = useState('simple');

	const location = useLocation();
	const { accept, beforeUpload, onUpload } = useUpload();
	const { conversationTitle, conversationType } = useAppContext();
	const { checkChatCount } = useCheckCount();

	const [count, setCount] = useState(0);

	const emptyQuestion = useMemo(() => isEmpty(question), [question]);
	const samples = useMemo<
		{
			kind: string;
			sample: string;
		}[]
	>(() => (type === 'all' ? questionSamples[0] : questionSamples[1][type]), [type]);

	/** 新会话 */
	const handleChat = async (title: string) => {
		const result = await checkChatCount(true);
		if (!result) {
			return;
		}
		conversationTitle.current = title.trim();
		conversationType.current = type;
		navigate(`/chat/${randomCode(11)}`);
	};

	const handleAddChat = () => {
		handleChat(question);
		if (!showSample) {
			onSend?.();
		}
	};

	const refreshSize = () => {
		validateToken().then(() => {
			const { usage } = getSessionData();
			if (usage?.fileSize && usage?.totalFileSize) {
				setCount(usage.fileSize);
			}
		});
	};

	useEffect(() => {
		if (loginUser?.username && !loginUser?.isVisitor && loginUser.datasetId) {
			refreshSize();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginUser, location]);

	return (
		<>
			<div className={classNames('ttn-conversation', { 'is-chat': showSample })}>
				<div className="tpyes">
					<ElIcon name="filter-list" className={classNames('filter', { active: type !== 'all' })} />
					<ElRadioGroup>
						<ElButton plain className={classNames('type-btn', { active: type === 'all' })} onClick={() => setType('all')}>
							全部
						</ElButton>

						{conversatonType.map((item, index) => (
							<ElButton key={item} plain className={classNames('type-btn', { active: type === index.toString() })} onClick={() => setType(index.toString())}>
								{item}
							</ElButton>
						))}
					</ElRadioGroup>
				</div>

				<div className="ttn-textArea">
					<div className="flex">
						<ElInput.TextArea
							value={question}
							plain
							placeholder="在此输入您的问题"
							rows={2}
							onChange={(value: string) => setQuestion(value)}
							onKeyDown={event => {
								if (event.key === 'Enter') {
									event.preventDefault();
									handleAddChat();
								}
							}}
						></ElInput.TextArea>
						<div className="operation">
							<div className="left">
								<ElSwitch value={deep} size="small" activeText="长思考" activeValue={true} inactiveValue={false} onChange={(value: boolean) => setDeep(value)} />
							</div>
							<div className="right">
								{type === 'all' && !deep && !loginUser?.isVisitor ? (
									<ElUpload
										className="el-button"
										style={{ padding: 0 }}
										action="#"
										accept={accept}
										showFileList={false}
										beforeUpload={beforeUpload}
										withCredentials
										onChange={onUpload}
									>
										<ElTooltip
											content={`支持PDF, Word, EXCEL（最大15MB）上传至个人知识库，当前剩余${formatNumber(count / 1024 / 1024, 2)} MB可用空间`}
											effect="grey"
											placement="top"
											popperStyle={{ maxWidth: 400 }}
										>
											<ElButton>
												<ElIcon prefix="fas" name="cloud-arrow-up" size="2x" />
											</ElButton>
										</ElTooltip>
									</ElUpload>
								) : null}
								<ElTooltip content={emptyQuestion ? '请输入问题' : '发送消息'} effect="grey" placement="top">
									<ElButton className={classNames({ 'is-disabled': emptyQuestion })} onClick={handleAddChat}>
										<ElIcon className="active" prefix="fas" name="paper-plane" size="2x" />
									</ElButton>
								</ElTooltip>
							</div>
						</div>
					</div>
				</div>

				{type === 'all' && (
					<div className="ttn-chat-type">
						<RadioGroup value={chatType} onChange={(value: string) => setChatType(value)} size="small">
							<Radio.Button value="simple">简洁</Radio.Button>
							<Radio.Button value="deep">深度探索</Radio.Button>
						</RadioGroup>
					</div>
				)}
			</div>

			{showSample && (
				<div className="ttn-question-sample">
					<div className="tip">
						您可以问我这些类型的问题：
						{/* <ElTooltip effect="grey" content={type === 'all' ? conversatonTip[0] : conversatonTip[1][type]} popperStyle={{ maxWidth: 400 }} placement="top">
							<ElIcon name="circle-info" prefix="fas" />
						</ElTooltip> */}
					</div>
					<div className="samples">
						{samples.map((item, index) => (
							<ElRow key={index}>
								<ElTag type="primary" disableTransitions>
									{item.kind}
								</ElTag>
								<ElLink underline onClick={() => handleChat(item.sample)}>
									{item.sample}
								</ElLink>
							</ElRow>
						))}
					</div>
				</div>
			)}
		</>
	);
};

export default Conversation;
